import { getLocalizedBackendMessage } from '@/utils/backendErrorMessages';

// Server text that is meant for a developer, not a user. A stack frame or an exception name in a
// toast tells the reader nothing they can act on and looks broken, so it never reaches the screen —
// it goes to the console instead.
const TECHNICAL_MARKERS = [
  'exception',
  'stack trace',
  'system.',
  'microsoft.',
  '   at ',
  'null reference',
  'object reference',
  'sqlexception',
  'inner exception',
];

// The permission gate is the one 570 that is not a validation message, and it has two outcomes.
// A refusal now names itself with `code` (AuthorizationRefusedException server-side); the bare
// title is the fallback for a server that predates the code, and matching on it is what the code
// exists to stop us doing.
const REFUSAL_CODE = 'Unauthorized';

// A 403 has two meanings and they need different sentences. The permission gate refuses a person;
// this one refuses the whole club, and telling an admin to ask an administrator for it sends them
// hunting a checkbox that is not theirs to tick. The server names it, so the two stay apart.
const FEATURE_BLOCKED_CODE = 'FeatureNotInPackage';

// Its other outcome: resolving permissions failed. That is our fault, not a detail the reader got
// wrong, and it reaches us as a fixed English sentence rather than anything translatable.
const SERVER_FAULT_TITLE = 'an error occurred please contact system administrator';

// A member delete refused because the member still owns memberships, money rows or live
// bookings. The server sends the counts by kind, so the sentence is built here in the reader's
// language rather than parsed.
const MEMBER_RELATED_CODE = 'MemberHasRelatedRecords';
const MEMBER_RELATED_KINDS = [
  'memberships', 'payments', 'sales', 'orders',
  'classBookings', 'waitingLists', 'groupEnrollments', 'classBookingRequests', 'membershipRequests',
];
// The club's walk-in customer, which every POS order without a customer is written against.
const MEMBER_WALK_IN_CODE = 'MemberIsWalkInCustomer';

// Read the same way everywhere in this file: the payload has arrived camelCased since the
// middleware wrote it, but the two readers below it already allowed for either.
function machineCodeOf(data) {
  return (data && (data.code || data.Code)) || '';
}

function describeMemberRelatedRecords(data, t, locale) {
  const related = (data && (data.related || data.Related)) || {};
  const records = MEMBER_RELATED_KINDS
    .filter((kind) => related[kind] > 0)
    .map((kind) => `${t(`errors.memberRelatedKinds.${kind}`)} (${related[kind]})`);
  if (records.length === 0) return '';

  return t('errors.memberHasRelatedRecords', { records: records.join(locale === 'ar' ? '، ' : ', ') });
}

function isPermissionRefusal(data, backendMessage) {
  const code = machineCodeOf(data);
  if (code) return code === REFUSAL_CODE;

  return String(backendMessage).trim().toLowerCase() === REFUSAL_CODE.toLowerCase();
}

function isFeatureBlocked(data) {
  const code = data && (data.code || data.Code);
  return code === FEATURE_BLOCKED_CODE;
}

function isServerFault(backendMessage) {
  return String(backendMessage).trim().toLowerCase().replace(/\.$/, '') === SERVER_FAULT_TITLE;
}

const BUSY_TEMPLATES = {
  load: 'errors.busy.load',
  save: 'errors.busy.save',
  delete: 'errors.busy.delete',
  phrase: 'errors.busy.phrase',
};

// The server's wait, in whole seconds, or null when it gave none.
export function retryAfterSeconds(error, now = Date.now()) {
  const headers = error && error.response && error.response.headers;
  if (!headers) return null;
  const value = typeof headers.get === 'function'
    ? headers.get('retry-after')
    : headers['retry-after'] || headers['Retry-After'];
  if (value === undefined || value === null || value === '') return null;

  const seconds = Number(value);
  if (Number.isFinite(seconds)) return seconds > 0 ? Math.ceil(seconds) : null;
  const left = Math.ceil((Date.parse(value) - now) / 1000);
  return left > 0 ? left : null;
}

function tooManyRequests(error, t, entity, verb, backendMessage) {
  const seconds = retryAfterSeconds(error);
  const retry = seconds ? t('errors.busy.retryIn', { seconds }) : t('errors.busy.retrySoon');
  if (entity) return t(BUSY_TEMPLATES[verb] || 'errors.busy.any', { subject: entity, retry });
  if (backendMessage) return backendMessage;
  return seconds ? t('errors.busy.any', { retry }) : t('errors.tooManyRequests');
}

export function looksTechnical(message) {
  if (!message) return false;
  const text = String(message).toLowerCase();
  if (TECHNICAL_MARKERS.some((marker) => text.includes(marker))) return true;
  // A wall of text is a dump, not a sentence someone wrote for a user.
  return text.length > 160;
}

/**
 * A sentence a user can act on, for any failed request.
 *
 * [entity] names what the request was about, in the user's language — "the members", "the cash
 * boxes" — and [verb] what was being done to it, so the toast reads "Couldn't load the members"
 * rather than announcing that something, somewhere, went wrong.
 */
export function friendlyError(error, t, { entity, verb = 'load', locale } = {}) {
  const template = {
    load: 'errors.loadFailed',
    save: 'errors.saveFailed',
    delete: 'errors.deleteFailed',
    prepare: 'errors.prepareFailed',
    apply: 'errors.applyFailed',
    // The subject already reads as a verb phrase ("sign you in"), so it supplies its own verb.
    phrase: 'errors.actionFailedSubject',
  }[verb] || 'errors.actionFailedSubject';

  const failed = () => (entity
    ? t(template, { subject: entity })
    : t('errors.actionFailed'));

  // Nothing to inspect: a caller reporting a failure it detected itself, not a rejected request.
  if (!error) return failed();

  // No response at all: the request never got an answer, which is a connection problem, not a
  // server fault, and the two need different advice.
  if (!error.response) return t('errors.offline');

  const status = error.response.status;
  const backendMessage = getLocalizedBackendMessage(error.response.data, locale, t);
  const usableBackendMessage = looksTechnical(backendMessage) ? '' : backendMessage;

  switch (true) {
    case status === 401:
      return t('errors.sessionExpired');
    case status === 403:
      if (isFeatureBlocked(error.response.data)) return t('errors.featureNotEnabled');
      return t('errors.notAllowed');
    // 570 is the API's validation status, not a refusal: its title is a sentence written for the
    // reader ("Date period is required"), and answering those with "you don't have permission"
    // sends someone to an administrator over a date they could have fixed themselves. Only the
    // permission gate's own two outcomes are not validation messages.
    case status === 570:
      if (isPermissionRefusal(error.response.data, backendMessage)) return t('errors.notAllowed');
      if (isServerFault(backendMessage)) return t('errors.serverFault');
      if (machineCodeOf(error.response.data) === MEMBER_WALK_IN_CODE) return t('errors.memberIsWalkInCustomer');
      if (machineCodeOf(error.response.data) === MEMBER_RELATED_CODE) {
        return describeMemberRelatedRecords(error.response.data, t, locale)
          || usableBackendMessage || t('errors.invalidDetails');
      }
      return usableBackendMessage || t('errors.invalidDetails');
    case status === 404:
      return entity ? t('errors.notFoundSubject', { subject: entity }) : t('errors.notFound');
    case status === 409:
      return usableBackendMessage || t('errors.conflict');
    case status === 400:
    case status === 422:
      return usableBackendMessage || t('errors.invalidDetails');
    case status === 429:
      return tooManyRequests(error, t, entity, verb, usableBackendMessage);
    default:
      // A server fault says nothing useful about itself, so name the action instead: the reader
      // needs to know what did not happen far more than why.
      return usableBackendMessage || failed();
  }
}
